import type { OrderDashboardIssue } from '@/api/hooks/useOrderDashboard'
import type { IssueBucketKey } from './issueCounts'

export const CONNECTION_LOGS_PATH = '/integration-management/connection-logs'

/** The Integration Logs page, filtered down to a single order. */
export function orderLogsPath(orderNumber: string): string {
  return `${CONNECTION_LOGS_PATH}?order=${encodeURIComponent(orderNumber)}`
}

/**
 * Header and accent colours per severity. Deliberately not the --theme-*
 * tokens: these are small labels and hairlines that have to stay legible on a
 * card in either theme, so they follow the chart-colour pattern of picking per
 * theme instead.
 */
const TONE_COLORS: Record<IssueBucketKey, { light: string; dark: string }> = {
  issues: { light: 'var(--foreground)', dark: 'var(--foreground)' },
  high: { light: '#cf1322', dark: '#ff7875' },
  medium: { light: '#d46b08', dark: '#ffa940' },
  low: { light: '#ad8b00', dark: '#ffd666' },
  warnings: { light: '#d46b08', dark: '#ffa940' },
}

export function issueToneColor(tone: IssueBucketKey, isDark: boolean): string {
  const color = TONE_COLORS[tone]
  return isDark ? color.dark : color.light
}

/**
 * Which count card an issue belongs to. A warning is a warning whatever
 * severity it carries, matching how the endpoint separates the two.
 */
export function issueTone(issue: OrderDashboardIssue): IssueBucketKey {
  if (issue.status === 'warning') return 'warnings'
  return issue.severity ?? 'issues'
}

export type IssueOwner = 'mine' | 'partner'

/**
 * Whose issue this is. An issue can be owned by both tenants at once, in
 * which case it is still ours to deal with -- the same rule the endpoint's
 * `my_issues` filter applies.
 */
export function issueOwner(
  issue: OrderDashboardIssue,
  tenantId: string | null | undefined,
): IssueOwner {
  if (!tenantId) return 'partner'
  return issue.issue_owners.includes(tenantId) ? 'mine' : 'partner'
}

/**
 * The tenant on the other end of the integration. Every log names both ends,
 * so which one is "the partner" depends on which end we are.
 */
export function otherTenantName(
  issue: OrderDashboardIssue,
  tenantId: string | null | undefined,
): string {
  const { origin_tenant_id, origin_tenant_name, destination_tenant_name } =
    issue.routing
  return origin_tenant_id === tenantId
    ? destination_tenant_name
    : origin_tenant_name
}

/** The order this issue is about, as the user knows it. */
export function issueOrderNumber(issue: OrderDashboardIssue): string | null {
  return issue.metadata.type === 'order'
    ? issue.metadata.origin_order_number
    : null
}

/** Whose issues the section is showing, in the toggle's own terms. */
export type IssueScope = 'all' | 'mine' | 'partner'

/**
 * Narrows a window's issues to one side of the partnership. This mirrors what
 * the endpoint's `my_issues`/`partner_issues` flags would do, but is applied
 * here so the toggle can show all three counts and switch without refetching.
 */
export function filterIssuesByScope(
  logs: OrderDashboardIssue[] | undefined,
  scope: IssueScope,
  tenantId: string | null | undefined,
): OrderDashboardIssue[] {
  const all = logs ?? []
  if (scope === 'all') return all
  return all.filter((log) => issueOwner(log, tenantId) === scope)
}

/**
 * Identifies an issue across refetches. Selection is held as a whole log, but
 * comparing by reference would drop the highlight the moment the list is
 * re-read, so both the list keys and the selection compare on this instead.
 */
export function issueKey(issue: OrderDashboardIssue): string {
  return issue._id ?? `${issue.saga_id}-${issue.date}`
}

/**
 * The tracked issues a log is an occurrence of. A log the tracker gives no
 * key (a warning, say) stands for its own issue, so it is still listed and
 * counted once rather than vanishing.
 */
export function issueGroupKeys(issue: OrderDashboardIssue): string[] {
  return issue.issue_keys?.length
    ? issue.issue_keys
    : [`log:${issueKey(issue)}`]
}

/**
 * What a log counts as when tallying unique issues. Unkeyed logs count as
 * themselves rather than by issueKey, which two logs missing an id and a date
 * would share.
 */
export function uniqueIssueIds(issue: OrderDashboardIssue): unknown[] {
  return issue.issue_keys?.length ? issue.issue_keys : [issue]
}

/**
 * Drops skipped events whose root failure is in the same set. A skip carries
 * its root's issue key but keeps its own severity and category (usually Low,
 * Request), so left in it would count and list the root issue under those
 * too. A skip whose root has been filtered away stays, and stands for itself.
 */
export function withoutEchoes(
  issues: OrderDashboardIssue[],
): OrderDashboardIssue[] {
  const rooted = new Set(
    issues.filter((issue) => !issue.skipped).flatMap((i) => i.issue_keys ?? []),
  )
  return issues.filter(
    (issue) =>
      !issue.skipped ||
      !issue.issue_keys?.length ||
      !issue.issue_keys.every((key) => rooted.has(key)),
  )
}
